import { t } from "@lingui/macro";
import {
  type BudgetovacPositionPriceFragment,
  type BudgetovacPositionsSetFragment,
  BudgetovacPositionsSetsDocument,
  type BudgetovacPositionsSetsQuery,
  type BudgetovacPositionsSetsQueryVariables,
  DeleteBudgetovacPositionsSetDocument,
  type DeleteBudgetovacPositionsSetMutation,
  type DeleteBudgetovacPositionsSetMutationVariables,
  SetActiveBudgetovacPositionsSetDocument,
  type SetActiveBudgetovacPositionsSetMutation,
  type SetActiveBudgetovacPositionsSetMutationVariables,
} from "@src/__generated__/graphql";
import type { BodyRow } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { uniqueId } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

type BudgetovacPositionsSetsStoreOptions = {
  readonly clientId?: string;
};

export enum BudgetovacPositionSetRowType {
  Group = "group",
  Row = "row",
  Subrow = "subrow",
}

export type BudgetovacPositionsRow =
  | BudgetovacPositionGroupRow
  | BudgetovacPositionsParentRow
  | BudgetovacPositionSubRow;

interface BudgetovacPositionGroupRow
  extends BodyRow,
    Pick<
      BudgetovacPositionsSetsQuery["budgetovacPositionsSets"][number],
      "client_id" | "brand"
    > {
  id: string;
  type: BudgetovacPositionSetRowType.Group;
  __subRows: BudgetovacPositionsParentRow[];
}

export interface BudgetovacPositionsParentRow
  extends BodyRow,
    BudgetovacPositionsSetFragment {
  type: BudgetovacPositionSetRowType.Row;
  __subRows: BudgetovacPositionSubRow[];
}

interface BudgetovacPositionSubRow
  extends Omit<BodyRow, "__subRows">,
    BudgetovacPositionPriceFragment {
  id: string;
  type: BudgetovacPositionSetRowType.Subrow;
}

export class BudgetovacPositionsSetsStore {
  clientId?: string;

  readonly tableKey = "budget-positions-sets-table";

  @observable.ref
  positionsSets: BudgetovacPositionGroupRow[] = [];

  @observable.ref
  searchTerm = "";

  deleteModalDisclosure = new DisclosureState<{ id: string }>();

  private fetcher = new FetchHelper<
    BudgetovacPositionsSetsQuery,
    BudgetovacPositionsSetsQueryVariables
  >(BudgetovacPositionsSetsDocument);

  private deleteMutator = new MutationHelper<
    DeleteBudgetovacPositionsSetMutation,
    DeleteBudgetovacPositionsSetMutationVariables
  >(DeleteBudgetovacPositionsSetDocument);

  private setActiveMutator = new MutationHelper<
    SetActiveBudgetovacPositionsSetMutation,
    SetActiveBudgetovacPositionsSetMutationVariables
  >(SetActiveBudgetovacPositionsSetDocument);

  constructor(
    private appStore: AppStore,
    options: BudgetovacPositionsSetsStoreOptions,
  ) {
    makeObservable(this);
    this.clientId = options.clientId;
    this.fetchData();
  }

  @computed
  get isLoading(): boolean {
    return this.fetcher.isLoading.value;
  }

  @computed
  get isDeleting(): boolean {
    return this.deleteMutator.isLoading.value;
  }

  @computed
  get isTogglingActive(): boolean {
    return this.setActiveMutator.isLoading.value;
  }

  @computed
  get getPositionsSetToDelete(): BudgetovacPositionsSetFragment | undefined {
    return this.positionsSets
      .flatMap((group) => group.__subRows)
      .find((ps) => ps.id === this.deleteModalDisclosure.additionalData?.id);
  }

  @action.bound
  handleSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.fetchData();
  }

  @action.bound
  async fetchData(): Promise<void> {
    const variables = {
      search: this.searchTerm,
      ...(this.clientId ? { client_id: this.clientId } : {}),
    };
    const [data, error] = await this.fetcher.fetch(variables);

    if (error || !data) return;

    this.positionsSets = data.budgetovacPositionsSets.map((group) => ({
      id: group.client_id ?? "default",
      client_id: group.client_id,
      brand: group.brand,
      type: BudgetovacPositionSetRowType.Group,
      __subRows: group.budgetovacPositionSets?.map((row) => ({
        ...row,
        type: BudgetovacPositionSetRowType.Row,
        __subRows: row.positions_prices?.map((subrow) => ({
          ...subrow,
          id: uniqueId(),
          type: BudgetovacPositionSetRowType.Subrow,
        })),
      })),
    }));
  }

  @action.bound
  openCreateModal(): void {
    this.appStore.budgetovacPositionsSetModalStore.modalState.onOpen({
      clientId: this.clientId,
      onSubmit: this.fetchData,
    });
  }

  @action.bound
  openEditModal(positionsSet: BudgetovacPositionsSetFragment): void {
    this.appStore.budgetovacPositionsSetModalStore.modalState.onOpen({
      positionsSet,
      clientId: this.clientId,
      onSubmit: this.fetchData,
    });
  }

  @action.bound
  openDuplicateModal(positionsSet: BudgetovacPositionsSetFragment): void {
    this.appStore.budgetovacPositionsSetModalStore.modalState.onOpen({
      positionsSet,
      clientId: this.clientId,
      onSubmit: this.fetchData,
      isDuplicate: true,
    });
  }

  @action.bound
  async deletePositionsSet(): Promise<void> {
    const id = this.deleteModalDisclosure.additionalData?.id;

    if (!id) {
      this.appStore.UIStore.toast({ status: "error" });
      return;
    }

    const [, error] = await this.deleteMutator.mutate({ id });

    if (error) return;

    this.appStore.UIStore.toast({
      title: t`Rate card deleted successfully`,
      status: "success",
    });

    this.deleteModalDisclosure.onClose();
    this.fetchData();
  }

  @action.bound
  async toggleActive(id: string, active: boolean): Promise<void> {
    const [, error] = await this.setActiveMutator.mutate({ id, active });

    if (error) return;

    this.fetchData();
  }
}
